import { observer } from "mobx-react-lite";
import React, { useEffect, useId, useState } from "react";
import ApiData from "../../api/ApiData";
import Msg from "../../api/Msg";
import { BtnPrimaryv4 } from "../UI/button/BtnPrimaryv4";
import { BtnOutlineV4 } from "../UI/button/BtnOutlineV4";
import { SideDrawer, SideDrawerCloseButton } from "@dmc/ui";
import { SiteFormField } from "../UI/SiteFormField";
import {
  addDays,
  diffDaysBetween,
  diffDaysFromToday,
  formatLocalDateInput,
  parseLocalDateInput,
  toLocalDateInputIso,
  tomorrowIso,
} from "../../utils/subscriptionPeriodDate";
import User from "../../api/User";
import {
  formatMoneyString,
  formatVatRate,
} from "../../utils/integration1cPriceDisplay";

const PRESETS = [
  // 14,
  365,
];
const DEFAULT_PERIOD = 365;

function Subscription1cPricesBlock({ prices, pricesLoading, pricesLoaded }) {
  if (pricesLoading) {
    return (
      <div className="subscription-1c-prices">
        <span className="subscription-1c-prices-status">Загрузка цен…</span>
      </div>
    );
  }

  if (!pricesLoaded) {
    return null;
  }

  if (!prices.length) {
    return (
      <div className="subscription-1c-prices">
        <span className="subscription-1c-prices-status">Цены не найдены</span>
      </div>
    );
  }

  return (
    <div className="subscription-1c-prices">
      {prices.map((item) => (
        <div
          key={item.nomenclature_uid ?? item.article ?? item.name}
          className="subscription-1c-price-block"
        >
          <div className="subscription-1c-price-name">{item.name}</div>
          <div className="subscription-1c-price-row">
            <span className="site-form-label">НДС</span>
            <span className="subscription-1c-price-value">
              {formatVatRate(item.vat_rate)}
            </span>
          </div>
          <div className="subscription-1c-price-row">
            <span className="site-form-label">Цена с НДС</span>
            <span className="subscription-1c-price-value">
              {formatMoneyString(item.price_with_vat)}
            </span>
          </div>
          <div className="subscription-1c-price-row">
            <span className="site-form-label">Цена без НДС</span>
            <span className="subscription-1c-price-value">
              {formatMoneyString(item.price_without_vat)}
            </span>
          </div>
        </div>
      ))}
    </div>
  );
}

export const GenerationSubscription = observer(
  ({
    title,
    flag,
    func,
    newFlag,
    prolongationBaseDate,
    load1cPrices = true,
  }) => {
    const isProlongation = Boolean(prolongationBaseDate);
    const titleId = useId();
    const [period, setPeriod] = useState(DEFAULT_PERIOD);
    const [description, setDescription] = useState("");
    const [activePreset, setActivePreset] = useState(null);

    const [calendar, setCalendar] = useState("");
    const [minDay, setMinDay] = useState("");

    const [prices, setPrices] = useState([]);
    const [pricesLoading, setPricesLoading] = useState(false);
    const [pricesLoaded, setPricesLoaded] = useState(false);

    useEffect(() => {
      if (!load1cPrices) {
        setPrices([]);
        setPricesLoaded(true);
        setPricesLoading(false);
        return;
      }

      let cancelled = false;
      setPrices([]);
      setPricesLoaded(false);
      setPricesLoading(true);

      ApiData.getIntegration1cPrices({ kind: "subscription" })
        .then((list) => {
          if (cancelled) return;
          const subscriptionPrices = (list ?? []).filter(
            (p) => p.kind === "subscription",
          );
          setPrices(subscriptionPrices);
          setPricesLoaded(true);
        })
        .finally(() => {
          if (!cancelled) {
            setPricesLoading(false);
          }
        });

      return () => {
        cancelled = true;
      };
    }, [load1cPrices]);

    useEffect(() => {
      if (isProlongation) {
        const baseIso = toLocalDateInputIso(prolongationBaseDate);
        const baseDate = parseLocalDateInput(baseIso);
        const min = formatLocalDateInput(addDays(baseDate, 1));
        setMinDay(min);
        setPeriod(DEFAULT_PERIOD);
        setActivePreset(null);
        setCalendar(formatLocalDateInput(addDays(baseDate, DEFAULT_PERIOD)));
      } else {
        const min = tomorrowIso();
        setMinDay(min);
        setPeriod(DEFAULT_PERIOD);
        setActivePreset(null);
        setCalendar(formatLocalDateInput(addDays(new Date(), DEFAULT_PERIOD)));
      }
      setDescription("");
    }, [isProlongation, prolongationBaseDate]);

    const applyPreset = (days) => {
      setPeriod(days);
      setActivePreset(days);
      if (isProlongation) {
        const baseDate = parseLocalDateInput(
          toLocalDateInputIso(prolongationBaseDate),
        );
        setCalendar(formatLocalDateInput(addDays(baseDate, days)));
      } else {
        setCalendar(formatLocalDateInput(addDays(new Date(), days)));
      }
    };

    const handlerPeriodCalendar = (e) => {
      const iso = e.target.value;
      setCalendar(iso);
      setActivePreset(null);
      if (isProlongation) {
        const baseIso = toLocalDateInputIso(prolongationBaseDate);
        setPeriod(diffDaysBetween(baseIso, iso));
      } else {
        setPeriod(diffDaysFromToday(iso));
      }
    };

    const close = () => flag(false);

    const handlerSubscription = () => {
      const periodNum = Number(period);
      if (!periodNum || periodNum < 1) {
        return Msg.writeMessages("Укажите корректный срок подписки.");
      }

      if (newFlag) {
        if (description === "") {
          return Msg.writeMessages(
            "Поле комментария обязательное для заполнения.",
          );
        }
        const data = {
          company_id: ApiData.company.company.id,
          descriptions: `Подписку активировал/обновил ${User.user.last_name} ${User.user.first_name} ${User.user.father_name}. ${description}`,
          valid_period: periodNum,
        };
        try {
          func(data);
          flag(false);
        } catch (e) {
          console.error(e);
        }
      } else {
        try {
          func(periodNum);
          flag(false);
        } catch (e) {
          console.error(e);
        }
      }
    };

    return (
      <SideDrawer
        title={title}
        titleId={titleId}
        onClose={close}
        usePortal={false}
        toolbar={<SideDrawerCloseButton onClick={close} title="Закрыть" />}
        footer={
          <div className="flex_center">
            <div className="card-flex-container_v2">
              <div>
                <SiteFormField label="Срок подписки" as="div">
                  <div className="subscription-period-chips">
                    {PRESETS.map((days) => (
                      <button
                        key={days}
                        type="button"
                        className={
                          activePreset === days
                            ? "subscription-period-chip subscription-period-chip--active"
                            : "subscription-period-chip"
                        }
                        onClick={() => applyPreset(days)}
                      >
                        {days === 365 ? "12 мес." : `${days} дней`}
                        {/* {days} дней */}
                      </button>
                    ))}
                  </div>
                </SiteFormField>

                <SiteFormField label="Или укажите дату окончания">
                  <input
                    value={calendar}
                    onChange={handlerPeriodCalendar}
                    className="subscription-date-input"
                    type="date"
                    min={minDay}
                  />
                  {minDay ? (
                    <span className="subscription-date-hint">
                      не ранее {minDay}
                    </span>
                  ) : null}
                </SiteFormField>
              </div>
              {newFlag ? (
                <SiteFormField label="Комментарий (Номер договора)">
                  <textarea
                    value={description}
                    placeholder="Укажите номер договора"
                    onChange={(e) => setDescription(e.target.value)}
                    className="subscription-contract-text"
                    rows={4}
                  />
                </SiteFormField>
              ) : null}
            </div>
            <div className="flx_btn_center">
              <BtnPrimaryv4
                onClick={handlerSubscription}
                text="Привязать подписку"
                footerSize
              />
              <BtnOutlineV4 onClick={close} text="Отмена" />
            </div>
          </div>
        }
      >
        <div className="site-modal-body">
          <div className="site-modal-fields subscription-modal-fields">
            {load1cPrices ? (
              <Subscription1cPricesBlock
                prices={prices}
                pricesLoading={pricesLoading}
                pricesLoaded={pricesLoaded}
              />
            ) : null}
          </div>
        </div>
      </SideDrawer>
    );
  },
);
